/* =============================================================================
 * chartTheme — token-driven colors for the app's charts (Recharts/Chart.js…).
 *
 * Two ways to use it:
 *   1. CSS-var strings (preferred where the lib renders to the DOM/SVG):
 *        import { CHART_COLORS, chartTokens } from 'ui/components';
 *        <Bar fill={CHART_COLORS[0]} />            // "var(--color-chart-1)"
 *   2. Resolved hex/oklch (when a lib needs a concrete color value):
 *        const theme = resolveChartTheme();        // reads computed :root vars
 *        <Bar fill={theme.colors[0]} />
 * ========================================================================== */

export const CHART_COLORS = [
  'var(--color-chart-1)',
  'var(--color-chart-2)',
  'var(--color-chart-3)',
  'var(--color-chart-4)',
  'var(--color-chart-5)',
  'var(--color-chart-6)',
  'var(--color-chart-7)',
  'var(--color-chart-8)',
];

export const chartTokens = {
  colors: CHART_COLORS,
  grid: 'var(--color-border)',
  axis: 'var(--color-foreground-subtle)',
  text: 'var(--color-foreground-muted)',
  tooltipBg: 'var(--color-popover)',
  tooltipBorder: 'var(--color-border)',
  tooltipText: 'var(--color-foreground)',
};

// Static fallbacks so charts have a color even before the DOM is measured.
const FALLBACKS = {
  colors: ['#4f46e5', '#3a8fb0', '#DDD2BA', '#A8C5FF', '#525252', '#10b981', '#f59e0b', '#ef4444'],
  grid: '#E5E5E5',
  axis: '#737373',
  text: '#525252',
  tooltipBg: '#ffffff',
  tooltipBorder: '#E5E5E5',
  tooltipText: '#0A0A0A',
};

/**
 * Resolve the chart tokens to concrete computed color strings from :root.
 * Falls back to static values when not running in a browser.
 */
export function resolveChartTheme(root) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return { ...FALLBACKS };
  const el = root || document.documentElement;
  const cs = getComputedStyle(el);
  const read = (name, fallback) => {
    const v = cs.getPropertyValue(name).trim();
    return v || fallback;
  };
  return {
    colors: FALLBACKS.colors.map((fb, i) => read(`--color-chart-${i + 1}`, fb)),
    grid: read('--color-border', FALLBACKS.grid),
    axis: read('--color-foreground-subtle', FALLBACKS.axis),
    text: read('--color-foreground-muted', FALLBACKS.text),
    tooltipBg: read('--color-popover', FALLBACKS.tooltipBg),
    tooltipBorder: read('--color-border', FALLBACKS.tooltipBorder),
    tooltipText: read('--color-foreground', FALLBACKS.tooltipText),
  };
}
